// @ts-strict-ignore
/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import clsx from 'clsx';

import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {Directions} from 'common/components/drop-menu/types';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {ChangeEvent} from 'common/components/select/types';
import {Placement} from 'common/components/tooltip/types';
import {CountBy, RetentionCountType} from 'common/query-builder/query-entry-section/types';
import {CountUsersOnceState} from 'common/report/constants';
import {ChartType, MetricType, RETENTION_MATH_TYPES} from 'common/types/reports/bookmark';
// eslint-disable-next-line import/no-cycle
import {getRetentionMetricAttributionItems} from 'common/report/insights/util/retention-attribution';
import {getCountUsersOnceTypeFromEntrySelection} from 'common/query-builder/query-block/count-menu/util';
import {useTranslation} from 'common/i18n/hooks';
import {QueryEntrySectionTrackingEvent} from 'common/query-builder/query-entry-section/tracking';
import {CountQueryBlock} from 'common/query-builder/query-block/count-menu/count-query-block';
import {useDataGroups} from 'common/contexts/react/data-group-context';
import {getFunnelOrRetentionDataGroupId} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/behavior-utils';
import {MultiAttributionMeasurement} from 'iron/common/query-builder/query-entry-section/row-types/new-metric-entry/common/multi-attribution';

import type {RowProps} from '../catalog';
import styles from './index.module.styl';

export function RetentionMetricMeasurement(props: RowProps) {
  const {t, print} = useTranslation();
  const dataGroupStore = useDataGroups();

  const dataGroupId = getFunnelOrRetentionDataGroupId({props});

  const handleRetentionCountBySelected = (countBy: CountBy) => {
    const measurement = props.entry.measurement;

    measurement.aggregationOperator = countBy.aggregationOperator;
    measurement.countUsersOnceType = getCountUsersOnceTypeFromEntrySelection({
      selectedCountUsersOnceType: countBy.countUsersOnceType,
      metricType: MetricType.Retention,
      multiAttribution: measurement.multiAttribution,
    });
    measurement.property = countBy.property;

    const entry = {
      ...props.entry,
      measurement,
    };
    props.updateEntry(entry);

    props.dispatchTrack(QueryEntrySectionTrackingEvent.SelectNumericAggregator, {
      /** @todo(metrics) tracking */
      // ...props.getEntryTrackingProps(),
      Aggregator: countBy.aggregationOperator,
      ...(countBy.property && {
        'Property name': countBy.property.label,
        'Property data type': `type` in countBy.property ? countBy.property.type : null,
      }),
    });
  };

  const handleRetentionBucketIndexSelected = (ev: ChangeEvent<number>) => {
    const retentionBucketIndex = ev.detail.item.value;
    const entry = {
      ...props.entry,
      measurement: {
        ...props.entry.measurement,
        retentionBucketIndex,
      },
    };
    props.updateEntry(entry);
  };

  const handleAttributionSelection = ({detail}: CustomEvent) => {
    const entry = {
      ...props.entry,
      measurement: {
        ...props.entry.measurement,
        attributionSelection: detail.item.value,
      },
    };
    props.updateEntry(entry);
  };

  const breakdownAttributionLabel = props.sectionProps.breakdownAttributionLabeler
    ? print(
        props.sectionProps.breakdownAttributionLabeler(
          print(getRetentionMetricAttributionItems(props.entry).find((entry) => entry.isSelected)?.label) || `...`,
        ),
      )
    : `...`;
  const COUNT_TYPE_MAPPING = {
    [RETENTION_MATH_TYPES.RETENTION_RATE]: RetentionCountType.RetentionRate,
    [RETENTION_MATH_TYPES.UNIQUE]: RetentionCountType.Unique,
    [RETENTION_MATH_TYPES.PROPERTY_AVERAGE]: RetentionCountType.PropertyAverage,
    [RETENTION_MATH_TYPES.PROPERTY_SUM]: RetentionCountType.PropertySum,
  };

  return (
    <div className={styles.measurement}>
      {props.children}
      {/* Measurement selection like Retention Rate, Property Aggregation, etc... */}
      {/* @todo(metrics) styles are messed up for measurement blocks */}
      <CountQueryBlock
        className={clsx(`count-menu`, styles.countBlock)}
        elref='countBlock'
        theme={ButtonTheme.Tertiary}
        buttonSize={ButtonSize.Inline}
        metricType={props.entry.behavior.type}
        dataGroupId={dataGroupId}
        dataGroups={Object.values(dataGroupStore.value)}
        projectId={props.sectionProps.projectId}
        countUsersOnceState={
          props.sectionProps.canUseMultiAttribution
            ? CountUsersOnceState.Required
            : props.sectionProps.countUsersOnceState
        }
        selected={{
          countType: COUNT_TYPE_MAPPING[props.entry.measurement.aggregationOperator],
          aggregationOperator: props.entry.measurement.aggregationOperator,
          property: props.entry.measurement.property,
          countUsersOnceType: props.entry.measurement.countUsersOnceType,
        }}
        eventNames={[props.entry.behavior.behaviors[1]?.name]}
        trackName='retention'
        userId={props.sectionProps.userId}
        workspaceId={props.sectionProps.workspaceId}
        onSelect={handleRetentionCountBySelected}
        includeCustomProperties={props.sectionProps.includeCustomProperties}
        showBorrowedProperties={props.sectionProps.showBorrowedProperties}
        withDataGroupSelect={props.sectionProps.metricLevelDataGroups}
      />

      {/* Retention bucket selector */}
      {props.sectionProps.chartType === ChartType.RetentionCurve ? (
        <SelectReact
          alwaysShowCaret
          theme={SelectTheme.Tertiary}
          size={ButtonSize.Small}
          menuAlignment={Directions.DownRight}
          menuWidth={184}
          disableFirstItemPadding
          elref='retentionBucketIndexSelect'
          sections={[
            {
              label: t(`query-builder.retention.groups.title`),
              icon: `info-xs`,
              tooltip: {
                'max-width': `120px`,
                placement: Placement.Right,
                text: t(`query-builder.retention.groups.tooltip`),
              },
              items: [
                {
                  label: t(`query-builder.retention.groups.all`),
                  isSelected: true,
                  value: null,
                },
              ],
            },
          ]}
        />
      ) : props.entry.measurement.retentionBuckets.length ? (
        <SelectReact
          alwaysShowCaret
          theme={SelectTheme.Tertiary}
          size={ButtonSize.Small}
          menuAlignment={Directions.DownRight}
          menuWidth={184}
          disableFirstItemPadding
          elref='retentionBucketIndexSelect'
          sections={[
            {
              label: t(`query-builder.retention.groups.title`),
              icon: `info-xs`,
              tooltip: {
                'max-width': `120px`,
                placement: Placement.Right,
                text: t(`query-builder.retention.groups.tooltip`),
              },
              items: props.entry.measurement.retentionBuckets,
            },
          ]}
          onChange={handleRetentionBucketIndexSelected}
        />
      ) : null}

      {/* Event attribution selection (selects breakdowns on Step 1 or Step 2) */}
      {!props.sectionProps.canUseMultiAttribution && props.sectionProps.canUseBreakdownAttribution && (
        <SelectReact
          elref='attributionSelect'
          alwaysShowCaret
          sections={[{items: getRetentionMetricAttributionItems(props.entry)}]}
          label={breakdownAttributionLabel}
          selectedOption=''
          size={ButtonSize.Small}
          theme={SelectTheme.Tertiary}
          menuWidth={0}
          tabIndex={0}
          experimentalPositioning
          onChange={handleAttributionSelection}
        />
      )}

      {/* Multi Attribution selection */}
      <MultiAttributionMeasurement {...props} extraLabel={breakdownAttributionLabel} />
    </div>
  );
}
